import { type StateCodeCA, type StateCodeUS, validStateCodesCA, validStateCodesUS } from '@cx-modules/constants';

import { type SupportedCountries } from './site-config-api';

export interface Subdivision {
  code: StateCodeCA | StateCodeUS;
  name: string;
}

export interface Country {
  code: SupportedCountries;
  name: string;
  subdivisions: Record<string, Subdivision>;
}

export const US_SUBDIVISION_NAMES: Record<StateCodeUS, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AS: 'American Samoa',
  AR: 'Arkansas',
  AZ: 'Arizona',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DC: 'District of Columbia',
  DE: 'Delaware',
  FL: 'Florida',
  FM: 'Federated States of Micronesia',
  GA: 'Georgia',
  GU: 'Guam',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NC: 'North Carolina',
  ND: 'North Dakota',
  NE: 'Nebraska',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NV: 'Nevada',
  NY: 'New York',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  PR: 'Puerto Rico',
  PW: 'Palau',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  VI: 'U.S. Virgin Islands',
  WA: 'Washington',
  WI: 'Wisconsin',
  WV: 'West Virginia',
  WY: 'Wyoming',
  MP: 'Northern Mariana Islands',
  MH: 'Marshall Islands',
};

export const CA_SUBDIVISION_NAMES: Record<StateCodeCA, string> = {
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  NT: 'Northwest Territories',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon',
};

const buildSubdivisions = <TCode extends StateCodeCA | StateCodeUS>(
  codes: readonly TCode[],
  names: Record<TCode, string>,
): Record<TCode, Subdivision> =>
  codes.reduce(
    (acc, code) => {
      acc[code] = { code, name: names[code] };
      return acc;
    },
    {} as Record<TCode, Subdivision>,
  );

export const countriesByCodeWithSubdivisions: {
  [key: string]: Country;
} = {
  US: {
    code: 'US',
    name: 'United States',
    subdivisions: buildSubdivisions(validStateCodesUS, US_SUBDIVISION_NAMES),
  },
  CA: {
    code: 'CA',
    name: 'Canada',
    subdivisions: buildSubdivisions(validStateCodesCA, CA_SUBDIVISION_NAMES),
  },
};
